/**
 * A carreira da conta, que vive no servidor.
 *
 * **Uma conta, uma carreira.** O servidor guarda uma por pessoa e a rota não
 * tem identificador: `/v1/career` é sempre a de quem está com a sessão na mão.
 * Trocar de carreira é sair da conta e entrar em outra.
 *
 * Quem simula continua sendo o motor, aqui no aparelho. O que mudou foi de
 * quem é a carreira: antes ela nascia e morria no IndexedDB, e a nuvem era um
 * extra vendido no passe. Agora o servidor é a fonte, e o IndexedDB virou
 * **cache**: ele existe para o Web Worker ter de onde abrir o mundo, e pode ser
 * apagado a qualquer momento sem perder nada.
 *
 * Duas regras merecem estar escritas aqui, porque são as que dão sentido ao
 * resto:
 *
 * 1. **Conflito é por hash, nunca por relógio.** Todo envio manda em `If-Match`
 *    o hash da versão que este aparelho acredita estar no servidor. Se o
 *    servidor estiver noutro ponto, a resposta é 409, e a saída é recarregar:
 *    o que está aqui é cache, e cache perde para a fonte.
 * 2. **Exportar é só ida.** `exportarCarreira` entrega uma cópia para o modo
 *    1 Jogador do aplicativo e o servidor anota a data. Não existe importar, e
 *    a ausência é proposital: sem ela, um arquivo editado no bloco de notas
 *    viraria título, posição no ranqueado e conquista.
 */

import { z } from "zod";

import { pedirArmazenamentoPersistente } from "../game/saves/espelho";
import { writeSave, type SaveRecord } from "../game/save-store";
import type { SaveMeta } from "../game/types";
import { t } from "../i18n";
import type { ClienteDaApi } from "./cliente";
import { ErroDaApi } from "./cliente";
import {
  CarreiraSchema,
  MundoDaCarreiraSchema,
  type Carreira,
  type MundoDaCarreira,
} from "./esquemas";

export type ClienteDeCarreira = Pick<ClienteDaApi, "pedir" | "enviarBytes" | "pedirVazio">;

export type { Carreira };

// ---------------------------------------------------------------------------
// O formato do arquivo
// ---------------------------------------------------------------------------

/**
 * Versão do envelope.
 *
 * Fica dentro do arquivo, e não só no nome da chave, porque quem baixa pode ser
 * uma build mais nova que a que enviou. Um número aqui é a diferença entre
 * recusar com clareza e montar um save com campos faltando.
 */
export const FORMATO_DO_ENVELOPE = 1;

const SaveMetaSchema = z.object({
  id: z.string(),
  clubName: z.string(),
  managerName: z.string(),
  season: z.number().int(),
  round: z.number().int(),
  totalRounds: z.number().int(),
  updatedAt: z.number(),
  schemaVersion: z.number().int(),
  engineVersion: z.string(),
  sizeBytes: z.number().int(),
});

const EnvelopeSchema = z.object({
  formato: z.literal(FORMATO_DO_ENVELOPE),
  meta: SaveMetaSchema,
  payload: z.string(),
});

type Envelope = z.infer<typeof EnvelopeSchema>;

function metaDoRegistro(registro: SaveRecord): SaveMeta {
  const { payload: _payload, ...meta } = registro;
  return meta;
}

/**
 * Comprime o envelope antes de mandar.
 *
 * O mundo serializado tem alguns megabytes de JSON, que é o tipo de texto que
 * encolhe muito. Quem joga no celular paga essa diferença em dados móveis a
 * cada envio, então ela não é detalhe.
 *
 * Sem `CompressionStream` os bytes vão crus, e isso não é erro: o servidor
 * guarda o arquivo sem olhar dentro, e o download reconhece os dois formatos
 * pela assinatura do gzip. Vale mais mandar grande do que não mandar.
 */
async function comprimir(texto: string): Promise<{ bytes: Uint8Array; tipo: string }> {
  const cru = new TextEncoder().encode(texto);
  if (typeof CompressionStream === "undefined") {
    return { bytes: cru, tipo: "application/octet-stream" };
  }
  const fluxo = new Blob([cru]).stream().pipeThrough(new CompressionStream("gzip"));
  const bytes = new Uint8Array(await new Response(fluxo).arrayBuffer());
  return { bytes, tipo: "application/gzip" };
}

/**
 * Cópia dos bytes num bloco só dela.
 *
 * Pelo mesmo motivo de `cliente.ts`: um `Uint8Array` apoiado em memória
 * compartilhada é recusado por parte dos motores, e por `Blob` e `crypto` até
 * no tipo.
 */
function comBlocoProprio(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  const copia = new Uint8Array(new ArrayBuffer(bytes.byteLength));
  copia.set(bytes);
  return copia;
}

/** O caminho de volta. A assinatura do gzip diz se há o que expandir. */
async function descomprimir(bytes: Uint8Array): Promise<string> {
  const ehGzip = bytes[0] === 0x1f && bytes[1] === 0x8b;
  if (!ehGzip || typeof DecompressionStream === "undefined") {
    return new TextDecoder().decode(bytes);
  }
  const fluxo = new Blob([comBlocoProprio(bytes)])
    .stream()
    .pipeThrough(new DecompressionStream("gzip"));
  return new Response(fluxo).text();
}

/**
 * Hash de reserva, para quando `crypto.subtle` não existir.
 *
 * `subtle` só aparece em contexto seguro, e o jogo servido por http numa rede
 * local não é um. Sem reserva, essas instalações não conseguiriam gravar
 * carreira nenhuma, porque o servidor exige o cabeçalho.
 *
 * São dois FNV-1a com constantes diferentes, e não um só: trinta e dois bits
 * colidem cedo demais para um valor que decide se uma carreira é sobrescrita.
 */
function hashDeReserva(bytes: Uint8Array): string {
  let a = 0x811c9dc5;
  let b = 0xcbf29ce4;
  for (const byte of bytes) {
    a = Math.imul(a ^ byte, 0x01000193) >>> 0;
    b = Math.imul(b ^ byte, 0x85ebca6b) >>> 0;
  }
  return a.toString(16).padStart(8, "0") + b.toString(16).padStart(8, "0");
}

/** Hash dos bytes que estão indo, que é o que o `X-Save-Hash` declara. */
export async function hashDosBytes(bytes: Uint8Array): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return hashDeReserva(bytes);

  const resumo = new Uint8Array(await subtle.digest("SHA-256", comBlocoProprio(bytes)));
  return [...resumo].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function lerEnvelope(bytes: Uint8Array): Promise<Envelope> {
  try {
    const lido = EnvelopeSchema.safeParse(JSON.parse(await descomprimir(bytes)));
    if (lido.success) return lido.data;
  } catch {
    // Cai no mesmo erro de baixo: para quem está esperando a carreira, "veio
    // quebrada" e "veio em formato desconhecido" são a mesma notícia.
  }
  throw new ErroDaApi("INTERNAL", t("carreira.servidor.arquivoIlegivel"), 0);
}

// ---------------------------------------------------------------------------
// A carreira
// ---------------------------------------------------------------------------

/**
 * Onde o mundo fica guardado neste aparelho enquanto se joga.
 *
 * O identificador leva a conta dentro porque o mesmo computador pode ser usado
 * por duas pessoas, e a carreira de uma não pode aparecer para a outra ao
 * trocar de conta. Não é segurança (quem tem o aparelho tem o arquivo): é não
 * mostrar o clube errado na tela de quem acabou de entrar.
 */
export function identificadorLocal(userId: string): string {
  return `carreira-${userId}`;
}

/**
 * A carreira da conta, ou `null` quando ainda não existe.
 *
 * 404 aqui não é erro: é a resposta certa para quem acabou de criar a conta e
 * ainda não começou. Quem chama precisa distinguir isso de "o servidor caiu",
 * e é por isso que só o 404 vira `null`.
 */
export async function carreiraDaConta(cliente: ClienteDeCarreira): Promise<Carreira | null> {
  try {
    return await cliente.pedir({ metodo: "GET", caminho: "/v1/career" }, CarreiraSchema);
  } catch (erro) {
    if (erro instanceof ErroDaApi && erro.status === 404) return null;
    throw erro;
  }
}

/**
 * Baixa o mundo do servidor e o deixa pronto para o motor abrir.
 *
 * A URL assinada é buscada com `fetch` puro, e não pelo cliente da API, por
 * duas razões: ela é absoluta e pode apontar para um bucket de outro domínio, e
 * quem autoriza é a assinatura. Mandar o token da pessoa junto seria entregá-lo
 * a um servidor que não precisa dele.
 */
export async function baixarCarreira(
  cliente: ClienteDeCarreira,
  userId: string,
): Promise<SaveRecord> {
  const entrega = await cliente.pedir(
    { metodo: "GET", caminho: "/v1/career/state" },
    MundoDaCarreiraSchema,
  );
  return aplicarMundo(entrega, userId);
}

export async function aplicarMundo(
  entrega: MundoDaCarreira,
  userId: string,
): Promise<SaveRecord> {
  let resposta: Response;
  try {
    resposta = await fetch(entrega.url);
  } catch {
    throw new ErroDaApi("REDE", t("carreira.servidor.semRede"), 0);
  }
  if (!resposta.ok) {
    throw new ErroDaApi("INTERNAL", t("carreira.servidor.arquivoIlegivel"), resposta.status);
  }

  const bytes = new Uint8Array(await resposta.arrayBuffer());
  if (bytes.byteLength !== entrega.sizeBytes) {
    throw new ErroDaApi("INTERNAL", t("carreira.servidor.downloadIncompleto"), resposta.status);
  }

  const envelope = await lerEnvelope(bytes);
  const registro: SaveRecord = {
    ...envelope.meta,
    id: identificadorLocal(userId),
    payload: envelope.payload,
  };
  await writeSave(registro);

  // O cache é descartável, mas perdê-lo no meio de uma rodada custa uma volta
  // ao servidor. Pedir persistência é o que dá para fazer daqui.
  void pedirArmazenamentoPersistente();

  return registro;
}

export interface EnvioDaCarreira {
  bytes: Uint8Array;
  tipoDoConteudo: string;
  hash: string;
  /** Metadado da carreira, que viaja na consulta porque o corpo é o arquivo. */
  consulta: URLSearchParams;
}

/** Prepara o arquivo que vai subir. */
export async function prepararCarreira(
  registro: SaveRecord,
  clubId: string,
): Promise<EnvioDaCarreira> {
  const meta = metaDoRegistro(registro);
  const envelope: Envelope = { formato: FORMATO_DO_ENVELOPE, meta, payload: registro.payload };
  const { bytes, tipo } = await comprimir(JSON.stringify(envelope));

  return {
    bytes,
    tipoDoConteudo: tipo,
    hash: await hashDosBytes(bytes),
    consulta: new URLSearchParams({
      managerName: meta.managerName,
      clubId,
      clubName: meta.clubName,
      season: String(meta.season),
      round: String(meta.round),
      schemaVersion: String(meta.schemaVersion),
      engineVersion: meta.engineVersion,
    }),
  };
}

/**
 * Grava a carreira no servidor.
 *
 * `hashConhecido` é o que este aparelho acredita estar lá. Ausente quer dizer
 * "estou criando agora", e o servidor recusa se já existir carreira: é o que
 * impede um aparelho que ficou dias fora do ar de sobrescrever a temporada
 * inteira jogada no outro.
 */
export async function enviarCarreira(
  cliente: ClienteDeCarreira,
  envio: EnvioDaCarreira,
  hashConhecido: string | null,
): Promise<Carreira> {
  const cabecalhos: Record<string, string> = { "X-Save-Hash": envio.hash };
  if (hashConhecido) cabecalhos["If-Match"] = hashConhecido;

  return cliente.enviarBytes(
    {
      metodo: "PUT",
      caminho: `/v1/career?${envio.consulta.toString()}`,
      bytes: envio.bytes,
      tipoDoConteudo: envio.tipoDoConteudo,
      cabecalhos,
    },
    CarreiraSchema,
  );
}

/**
 * Pede a cópia para jogar no modo 1 Jogador.
 *
 * O servidor anota a data da saída. Essa anotação não serve para sincronizar
 * nada, porque nada volta: serve para o suporte entender o que houve quando
 * alguém disser que perdeu progresso depois de jogar offline.
 */
export function exportarCarreira(cliente: ClienteDeCarreira): Promise<MundoDaCarreira> {
  return cliente.pedir({ metodo: "POST", caminho: "/v1/career/export" }, MundoDaCarreiraSchema);
}

/** Apaga a carreira da conta. É o único jeito de recomeçar sem trocar de conta. */
export function apagarCarreira(cliente: ClienteDeCarreira): Promise<void> {
  return cliente.pedirVazio({ metodo: "DELETE", caminho: "/v1/career" });
}
